// 다운로드 이미지 우하단에 찍는 브랜드 서명. 브라우저 canvas 로 합성해서
// 서버 대역폭을 쓰지 않는다.
//
// **전환 장치가 아니라 서명이다.** 예전엔 Pro 결제자만 면제되는 반투명 검정 pill
// 배지였는데 (짧은 변의 2.8%, 세리프), 구독을 접은 뒤로는 아무도 벗어날 수 없는
// 조건이 되어 "Pro 로 업그레이드하면 없어집니다" 라며 팔지 않는 상품을 가리켰다.
// 지금은 가입 여부와 무관하게 전원에게 같은 서명이 붙는다 — 게이팅이 아니므로
// 조건 분기가 없다.
//
// 크기·불투명도는 실측으로 골랐다. 1.1% 는 카톡 썸네일로 축소되면 사라져서 브랜드
// 역할을 못 하고, 1.8% 는 사진과 경쟁한다. 1.4% / 85% 가 "폰에서 꽉 채워 볼 때
// 읽히고 사진을 해치지 않는" 지점이다.

const WATERMARK_TEXT = 'archelier';

// 짧은 변 대비 글자 크기 / 여백. 종횡비가 달라도 비슷하게 읽히도록 짧은 변 기준.
const FONT_RATIO = 0.014;
const MARGIN_RATIO = 0.028;
const MIN_FONT_PX = 11;

// 서명이 놓일 자리의 밝기를 읽어 글자색을 뒤집는다. 판(pill)을 쓰지 않기로 했으므로
// 흰 글씨 하나로는 밝은 렌더에서 사라진다 — 인테리어는 흰 벽이 흔해서 실제로
// 그랬다 (#f7f5f1 배경에서 그림자만 남고 글자가 안 읽혔다).
const LIGHT_BG_THRESHOLD = 0.62;
const LIGHT_TEXT = 'rgba(255, 255, 255, 0.85)';
const DARK_TEXT = 'rgba(18, 18, 18, 0.62)';        // --primary 잉크
const LIGHT_SHADOW = 'rgba(0, 0, 0, 0.45)';
const DARK_SHADOW = 'rgba(255, 255, 255, 0.55)';

// sRGB 상대 휘도. 정확한 감마 보정까지 갈 필요는 없고, 흰 벽 / 어두운 바닥을
// 가르는 정도면 충분하다.
function meanLuminance(ctx, x, y, w, h) {
  const cw = Math.max(1, Math.round(w));
  const ch = Math.max(1, Math.round(h));
  const { data } = ctx.getImageData(Math.max(0, Math.round(x)), Math.max(0, Math.round(y)), cw, ch);
  let sum = 0;
  // 4px 간격 샘플링 — 전 픽셀을 돌 이유가 없다.
  const step = 4 * 4;
  let n = 0;
  for (let i = 0; i < data.length; i += step) {
    sum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
    n++;
  }
  return n ? sum / n : 0;
}

// 앱과 같은 서체. canvas 는 CSS @font-face 를 자동으로 기다려 주지 않아서,
// 로드 전에 measureText / fillText 를 호출하면 조용히 시스템 sans 로 폴백한다.
// 그러면 서명만 다른 서체로 찍혀 나간다 — 그래서 먼저 명시적으로 기다린다.
const FONT_FAMILY = 'Gabarito, system-ui, -apple-system, sans-serif';

async function ensureFont(fontSize) {
  if (!document.fonts?.load) return;
  try {
    // 실제 문자열을 함께 넘긴다 — Gabarito 는 unicode-range 로 두 subset
    // (Gabarito-00 / -01) 으로 쪼개져 있어서, 텍스트를 주지 않으면 필요한 쪽이
    // 안 올 수 있다 (src/styles/fonts.css).
    await document.fonts.load(`600 ${fontSize}px Gabarito`, WATERMARK_TEXT);
  } catch {
    /* 폴백 서체로 찍히더라도 다운로드 자체를 막지는 않는다 */
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function compositeWatermark(imageUrl) {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const img = await loadImage(objectUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const base = Math.min(canvas.width, canvas.height);
    const fontSize = Math.max(MIN_FONT_PX, Math.round(base * FONT_RATIO));
    const margin = Math.round(base * MARGIN_RATIO);

    await ensureFont(fontSize);

    ctx.font = `600 ${fontSize}px ${FONT_FAMILY}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';

    // 글자가 실제로 덮을 영역만 재서 그 밝기로 색을 정한다. 코너 전체를 재면
    // 밝은 벽 + 어두운 가구가 섞여 평균이 중간으로 뭉개진다.
    const textWidth = ctx.measureText(WATERMARK_TEXT).width;
    const bgLuminance = meanLuminance(
      ctx,
      canvas.width - margin - textWidth,
      canvas.height - margin - fontSize,
      textWidth,
      fontSize,
    );
    const onLightBg = bgLuminance > LIGHT_BG_THRESHOLD;

    // 판 대신 반대색 그림자로만 형태를 잡는다 — 판을 깔면 다시 배지가 된다.
    ctx.shadowColor = onLightBg ? DARK_SHADOW : LIGHT_SHADOW;
    ctx.shadowBlur = Math.round(fontSize * 0.5);
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = onLightBg ? DARK_TEXT : LIGHT_TEXT;
    ctx.fillText(WATERMARK_TEXT, canvas.width - margin, canvas.height - margin);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    return await new Promise((resolve, reject) => {
      canvas.toBlob((b) => {
        if (b) resolve(b);
        else reject(new Error('WATERMARK_ENCODE_FAILED'));
      }, 'image/jpeg', 0.92);
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
